/**
 * TaxRateSelect - iter971k-E
 * ==========================
 *
 * A single-row VAT/WHT dropdown for the Bank Feed table & card views.
 *
 * Behaviour:
 *   • Native-styled shadcn <Select> with a visible chevron arrow so the
 *     dropdown affordance is unmistakable.
 *   • Options come from `taxCtx.vat_options` / `taxCtx.wht_options`,
 *     which the backend builds from the country's active tax pack.
 *   • Always renders - column visibility is decided upstream (per country).
 *   • When Auto-Apply (tax tracking) is OFF, no AI suggestion decoration
 *     is shown. When ON, an unconfirmed suggestion gets a dashed border +
 *     Sparkles hint.
 *   • On change, POST /api/bank-feeds/save-changes/{tx_id} runs an atomic
 *     journal reverse + repost. `onSaved` is invoked so the parent can
 *     refetch the list.
 *   • HARD blockers (reconciled, period lock, reversed, bank_charge,
 *     payroll) disable the dropdown and surface the reason as a tooltip.
 *
 * Contract with parents:
 *   <TaxRateSelect
 *     kind="vat" | "wht"
 *     transaction={tx}
 *     taxCtx={taxCtx}              // /api/bank-feeds/tax-context payload
 *     taxStatus={taxStatus[tx.id]} // /api/bank-feeds/tax-status/... payload
 *     onFetchStatus={id => void}   // optional - lazy-loads status if absent
 *     onSaved={payload => void}    // optional - parent refetches list
 *   />
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Sparkles, Lock, Loader2 } from 'lucide-react';
import { PeriodLockWarningDialog } from './PeriodLockWarningDialog';

const NO_TAX_VALUE = 'none';

// Round-trip a rate coming from either the tax pack (percentage, e.g. 7.5)
// or the BFT (fraction, e.g. 0.075) to a percentage number.
const toPct = (r) => {
  const n = Number(r || 0);
  if (!n) return 0;
  return n < 1 ? n * 100 : n;
};

// Format a percentage without any rounding - keep 7.5 as "7.5%", 5 as "5%",
// 2.5 as "2.5%". Never approximate (previous `.toFixed(0)` rounded 7.5 to 8).
const fmtPct = (pct) => {
  const n = Number(pct || 0);
  if (!n) return '0%';
  // Trim trailing zeros ("7.50" → "7.5", "5.0" → "5").
  return `${Number.isInteger(n) ? n : parseFloat(n.toFixed(4))}%`;
};

// Find the option that best matches the currently-applied rate.
const matchOption = (options, rate, kind) => {
  if (!rate || rate <= 0) return NO_TAX_VALUE;
  const pct = toPct(rate);
  // Prefer an EXACT-rate match; fall back to nearest within 0.5%.
  const exact = options.find((o) => o.value !== NO_TAX_VALUE && Math.abs(o.rate - pct) < 0.05);
  if (exact) return exact.value;
  const nearest = options
    .filter((o) => o.value !== NO_TAX_VALUE)
    .sort((a, b) => Math.abs(a.rate - pct) - Math.abs(b.rate - pct))[0];
  if (nearest && Math.abs(nearest.rate - pct) < 0.5) return nearest.value;
  // No good match - treat as "no tax" so the dropdown shows a clean label.
  return NO_TAX_VALUE;
};

export const TaxRateSelect = ({
  kind,           // 'vat' | 'wht'
  transaction,
  taxCtx,
  taxStatus,
  onFetchStatus,
  onSaved,
}) => {
  const isVat = kind === 'vat';
  const label = isVat ? (taxCtx?.vat_label || 'VAT') : 'WHT';
  const options = useMemo(
    () => (isVat ? (taxCtx?.vat_options || []) : (taxCtx?.wht_options || [])),
    [isVat, taxCtx],
  );
  const autoApplyEnabled = taxCtx?.auto_apply_enabled !== false;

  // Lazy-load tax status if the parent didn't pass one.
  const fetchAttemptedRef = useRef(false);
  useEffect(() => {
    if (!taxStatus && !fetchAttemptedRef.current && onFetchStatus && transaction?.id) {
      fetchAttemptedRef.current = true;
      onFetchStatus(transaction.id);
    }
  }, [transaction?.id, taxStatus, onFetchStatus]);

  // ── Current applied state - tx-first, then tax-status ──
  const appliedRate = useMemo(() => {
    if (isVat) {
      if (transaction?.vat_applied || transaction?.apply_input_vat || transaction?.apply_output_vat) {
        return toPct(transaction?.input_vat_rate ?? transaction?.vat_rate ?? taxStatus?.vat_rate ?? 0);
      }
      return toPct(taxStatus?.has_vat ? (taxStatus?.vat_rate ?? 0) : 0);
    }
    if (transaction?.wht_applied || transaction?.apply_wht || transaction?.customer_is_wht_agent) {
      return toPct(transaction?.wht_rate ?? taxStatus?.wht_rate ?? 0);
    }
    return toPct(taxStatus?.has_wht ? (taxStatus?.wht_rate ?? 0) : 0);
  }, [isVat, transaction, taxStatus]);

  const currentValue = useMemo(
    () => matchOption(options, appliedRate, kind),
    [options, appliedRate, kind],
  );

  const [pendingValue, setPendingValue] = useState(null);
  const [saving, setSaving] = useState(false);
  // iter971k-H - Period Lock UX. If the tx date falls in an annual-locked
  // period the backend `save-changes` returns 423. Rather than a lonely
  // toast, we open the same PeriodLockWarningDialog the Record modals use
  // so the UX is consistent across the whole app.
  const [lockDialog, setLockDialog] = useState({
    open: false,
    lockedPeriod: '',
    pendingValue: null,
  });

  const closeLockDialog = () => setLockDialog(prev => ({ ...prev, open: false }));

  const displayValue = pendingValue ?? currentValue;

  // ── Suggestion state ──
  // iter 1005 - `dismissed` gives instant UI feedback when the user rejects
  // the suggestion (persisted server-side via dismiss_*_suggestion).
  const [dismissed, setDismissed] = useState(false);
  const suggestedRate = isVat ? taxStatus?.suggested_vat_rate : taxStatus?.suggested_wht_rate;
  const isSuggested = autoApplyEnabled && !dismissed && !!(isVat ? taxStatus?.suggested_vat : taxStatus?.suggested_wht);
  const suggestionUnapplied = isSuggested && currentValue === NO_TAX_VALUE && suggestedRate > 0;

  // ── Hard blockers only - soft blocks are advisory, dropdown stays open ──
  const hardBlocked = !!(isVat ? taxStatus?.blocked_hard_vat : taxStatus?.blocked_hard_wht);
  const hardReason = (isVat ? taxStatus?.hard_reason_vat : taxStatus?.hard_reason_wht)
    || (isVat ? taxStatus?.disabled_reason_vat : taxStatus?.disabled_reason_wht);
  const bankChargeVat = isVat && transaction?.is_bank_charge;
  const reconciled = !!transaction?.reconciled;
  const periodLocked = !!transaction?.period_locked;
  const disabled = hardBlocked || bankChargeVat || reconciled || periodLocked || saving;

  const disabledReason = hardReason
    || (bankChargeVat && 'Bank charges already include VAT split: dropdown not applicable.')
    || (reconciled && 'This transaction is reconciled: unreconcile to change tax.')
    || (periodLocked && 'Period is locked: reopen to change tax.')
    || null;

  const performSave = async (newValue) => {
    const option = options.find((o) => o.value === newValue);
    if (!option) return;
    setPendingValue(newValue);
    setSaving(true);
    try {
      const appliedFlag = option.value !== NO_TAX_VALUE;
      const ratePct = appliedFlag ? Number(option.rate) : 0;
      const rateFrac = ratePct > 0 ? ratePct / 100 : 0;
      // Preserve the *other* tax kind's current state so we don't
      // accidentally undo it.
      const body = {
        vat_applied: isVat
          ? appliedFlag
          : !!(transaction?.vat_applied || transaction?.apply_input_vat || transaction?.apply_output_vat || taxStatus?.has_vat),
        wht_applied: !isVat
          ? appliedFlag
          : !!(transaction?.wht_applied || transaction?.apply_wht || transaction?.customer_is_wht_agent || taxStatus?.has_wht),
        vat_rate: isVat
          ? rateFrac
          : ((transaction?.input_vat_rate ?? transaction?.vat_rate ?? taxStatus?.vat_rate ?? 0) / (transaction?.input_vat_rate > 1 ? 100 : 1)),
        wht_rate: !isVat
          ? rateFrac
          : ((transaction?.wht_rate ?? taxStatus?.wht_rate ?? 0) / (transaction?.wht_rate > 1 ? 100 : 1)),
      };
      const res = await axios.post(`${API}/bank-feeds/save-changes/${transaction.id}`, body);
      toast.success(res.data?.message || `${label} updated to ${option.label}`);
      onSaved?.(res.data);
      // Re-evaluate this row's tax status (server recomputed eligibility on save).
      if (onFetchStatus && transaction?.id) onFetchStatus(transaction.id, true);
    } catch (err) {
      setPendingValue(null);
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 423 || (typeof msg === 'string' && /period|locked/i.test(msg))) {
        // Extract the locked period from the error detail if possible.
        const match = typeof msg === 'string' ? msg.match(/(\d{4}-\d{2})/) : null;
        const txDate = transaction?.transaction_date || transaction?.date || '';
        setLockDialog({
          open: true,
          lockedPeriod: (match && match[1]) || (txDate ? txDate.slice(0, 7) : ''),
          pendingValue: newValue,
        });
      } else {
        toast.error(msg || `Failed to update ${label}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChange = async (newValue) => {
    if (disabled) return;
    if (newValue === currentValue) {
      // iter 1005 - picking "No VAT/WHT" while a suggestion is showing is a
      // REJECTION of the suggestion. Persist the dismissal so the hint never
      // comes back (previously this was a no-op and the hint was permanent).
      if (newValue === NO_TAX_VALUE && suggestionUnapplied) {
        setSaving(true);
        try {
          const body = isVat ? { dismiss_vat_suggestion: true } : { dismiss_wht_suggestion: true };
          await axios.post(`${API}/bank-feeds/save-changes/${transaction.id}`, body);
          setDismissed(true);
          toast.success(`Suggestion dismissed: ${label} stays off for this transaction`);
          onSaved?.();
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Could not dismiss the suggestion');
        } finally {
          setSaving(false);
        }
      }
      return;
    }
    await performSave(newValue);
  };

  // ── Trigger styling - surface suggestion hint when applicable ──
  const triggerCls = [
    'h-7 min-w-[132px] max-w-[180px] px-2 py-0 text-[11px] font-medium gap-1',
    'shadow-none',
    disabled ? 'opacity-60 cursor-not-allowed' : '',
    suggestionUnapplied
      ? (isVat
          ? 'border-emerald-300 border-dashed ring-1 ring-emerald-200/60 bg-emerald-50/40 text-emerald-800'
          : 'border-amber-300 border-dashed ring-1 ring-amber-200/60 bg-amber-50/40 text-amber-800')
      : '',
    currentValue !== NO_TAX_VALUE
      ? (isVat ? 'border-emerald-200 bg-emerald-50/60 text-emerald-800' : 'border-amber-200 bg-amber-50/60 text-amber-800')
      : '',
  ].filter(Boolean).join(' ');

  const inner = (
    // iter 1005 - while an unapplied suggestion is showing, the Select is
    // rendered value-less so that re-picking "No VAT/WHT" still fires
    // onValueChange (Radix swallows same-value selections) → dismissal works.
    <Select value={suggestionUnapplied ? '' : displayValue} onValueChange={handleChange} disabled={disabled}>
      <SelectTrigger
        className={triggerCls}
        aria-label={`${label} rate`}
        data-testid={`tax-select-${kind}-${transaction.id}`}
      >
        {saving ? (
          <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
        ) : suggestionUnapplied ? (
          // iter 1005 - truth first: always show the ACTUAL selection; the
          // suggestion is a sparkle hint, never the face value.
          <span className="inline-flex items-center gap-1 truncate">
            <span className="truncate">
              {options.find((o) => o.value === displayValue)?.label || `No ${label}`}
            </span>
            <Sparkles className={`w-2.5 h-2.5 shrink-0 ${isVat ? 'text-emerald-600' : 'text-amber-600'}`} />
          </span>
        ) : disabled && disabledReason ? (
          <span className="inline-flex items-center gap-1 truncate">
            <Lock className="w-2.5 h-2.5 shrink-0" />
            <span className="truncate">
              {options.find((o) => o.value === displayValue)?.label || `No ${label}`}
            </span>
          </span>
        ) : (
          <SelectValue placeholder={`No ${label}`}>
            <span className="truncate">
              {options.find((o) => o.value === displayValue)?.label || `No ${label}`}
            </span>
          </SelectValue>
        )}
      </SelectTrigger>
      <SelectContent className="max-w-[260px]" data-testid={`tax-select-content-${kind}-${transaction.id}`}>
        {options.map((opt) => {
          const isSuggestedOpt = suggestionUnapplied
            && opt.value !== NO_TAX_VALUE
            && Math.abs(Number(opt.rate || 0) - Number(suggestedRate || 0)) < 0.05;
          return (
            <SelectItem
              key={opt.value}
              value={opt.value}
              className="text-xs"
              data-testid={`tax-option-${kind}-${opt.value}-${transaction.id}`}
            >
              <span className="inline-flex items-center gap-1">
                {opt.label}
                {isSuggestedOpt && (
                  <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${isVat ? 'text-emerald-600' : 'text-amber-600'}`}>
                    <Sparkles className="w-2.5 h-2.5" /> suggested
                  </span>
                )}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );

  // Tooltip surfaces the disabled reason OR the suggestion hint.
  const offBySetup = isVat ? taxCtx?.vat_by_setup === false : taxCtx?.wht_by_setup === false;
  const tooltipText = disabled && disabledReason
    ? disabledReason
    : suggestionUnapplied
    ? `AkountFly suggests ${label} at ${fmtPct(suggestedRate)}: open the dropdown to apply it, or pick "No ${label}" to dismiss this hint.`
    : offBySetup && currentValue === NO_TAX_VALUE
    ? `Your tax setup says ${label} does not normally apply to you, so nothing is suggested automatically. You can still pick a rate here for an exception - or change the answer in Settings → Tax.`
    : null;

  if (!tooltipText) return (
    <>
      {inner}
      <PeriodLockWarningDialog
        open={lockDialog.open}
        onClose={closeLockDialog}
        transactionDate={transaction?.transaction_date || transaction?.date || ''}
        lockedPeriod={lockDialog.lockedPeriod}
        onPostToOriginalDate={null}
        onPostToToday={null}
        informationalOnly
      />
    </>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{inner}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-[260px]" data-testid={`tax-select-tip-${kind}-${transaction.id}`}>
          {tooltipText}
        </TooltipContent>
      </Tooltip>
      <PeriodLockWarningDialog
        open={lockDialog.open}
        onClose={closeLockDialog}
        transactionDate={transaction?.transaction_date || transaction?.date || ''}
        lockedPeriod={lockDialog.lockedPeriod}
        onPostToOriginalDate={null}
        onPostToToday={null}
        informationalOnly
      />
    </TooltipProvider>
  );
};

export default TaxRateSelect;
